import { QuoteStatus, UnitCommercialStatus, type Currency } from '../enums';
import { isNullDate, monthKey } from './dates';
import { isAnnulledDeal, isSeparation } from './stages';
import type { FunnelConfig, FunnelDeal } from './types';

/** Precio final de una proforma: lista − descuento (nunca negativo). */
export function quoteFinalPrice(priceList: number, discount: number): number {
  return Math.max(0, Math.round((priceList - Math.max(0, discount)) * 100) / 100);
}

/** Estado efectivo de una proforma en un instante dado (la expiración se evalúa, no se confía en la fila). */
export function effectiveQuoteStatus(
  q: { status: QuoteStatus; expiresAt: Date | null },
  now = new Date(),
): QuoteStatus {
  if (q.status !== QuoteStatus.VIGENTE) return q.status;
  if (!isNullDate(q.expiresAt) && q.expiresAt!.getTime() < now.getTime())
    return QuoteStatus.EXPIRADA;
  return QuoteStatus.VIGENTE;
}

/** Una unidad se puede proformar / separar si está disponible y no está en blacklist. */
export function isUnitAvailable(u: {
  commercialStatus: UnitCommercialStatus;
  isBlacklisted: boolean;
  deletedAt?: Date | null;
}): boolean {
  return !u.deletedAt && !u.isBlacklisted && u.commercialStatus === UnitCommercialStatus.DISPONIBLE;
}

/**
 * Estado comercial que corresponde a una unidad según sus procesos vivos: vendida > separada > bloqueada > disponible.
 * Es la fuente de verdad al devolver / anular: nunca se "pinta" a mano.
 */
export function deriveUnitStatus(u: {
  isBlacklisted: boolean;
  hasActiveSale: boolean;
  hasActiveSeparation: boolean;
}): UnitCommercialStatus {
  if (u.hasActiveSale) return UnitCommercialStatus.VENDIDO;
  if (u.hasActiveSeparation) return UnitCommercialStatus.SEPARADO;
  if (u.isBlacklisted) return UnitCommercialStatus.BLOQUEADO;
  return UnitCommercialStatus.DISPONIBLE;
}

/** Convierte un monto entre soles y dólares con el tipo de cambio de la empresa (misma moneda = sin cambio). */
export function convertAmount(
  amount: number,
  from: Currency,
  to: Currency,
  penPerUsd: number,
): number {
  if (from === to) return amount;
  const usd = from === 'PEN' ? amount / penPerUsd : amount;
  return Math.round((to === 'PEN' ? usd * penPerUsd : usd) * 100) / 100;
}

/**
 * Ticket de un proceso (Lote 5 · H2, P15): la suma de los precios por unidad (`deal_units.price`), cada uno en la
 * moneda de su unidad, llevada a una sola moneda con el tipo de cambio de la empresa. Null si ninguna unidad tiene
 * precio: quien llama recurre al total del proceso o a la proforma como respaldo.
 */
export function unitsTicket(
  units: ReadonlyArray<{ price: number | null; currency: Currency }>,
  to: Currency,
  penPerUsd: number,
): number | null {
  const priced = units.filter((u) => u.price !== null && Number.isFinite(u.price));
  if (priced.length === 0) return null;
  const total = priced.reduce(
    (sum, u) => sum + convertAmount(u.price!, u.currency, to, penPerUsd),
    0,
  );
  return Math.round(total * 100) / 100;
}

// ---------------------------------------------------------------- perfil del comprador

export const AGE_BANDS = ['<30', '30-39', '40-49', '50-59', '60+', 'SIN_DATO'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];

export function ageBand(birthDate: Date | null | undefined, at = new Date()): AgeBand {
  if (!birthDate || isNullDate(birthDate)) return 'SIN_DATO';
  let age = at.getUTCFullYear() - birthDate.getUTCFullYear();
  const m = at.getUTCMonth() - birthDate.getUTCMonth();
  if (m < 0 || (m === 0 && at.getUTCDate() < birthDate.getUTCDate())) age -= 1;
  if (age < 30) return '<30';
  if (age < 40) return '30-39';
  if (age < 50) return '40-49';
  if (age < 60) return '50-59';
  return '60+';
}

export const TICKET_BANDS = [
  '<100k',
  '100-150k',
  '150-200k',
  '200-300k',
  '300k+',
  'SIN_DATO',
] as const;
export type TicketBand = (typeof TICKET_BANDS)[number];

/** Banda de ticket en USD (montos en PEN se convierten con el tipo de cambio indicado). */
export function ticketBand(
  amount: number | null,
  currency: 'USD' | 'PEN',
  penPerUsd = 3.75,
): TicketBand {
  if (amount === null || !Number.isFinite(amount) || amount <= 0) return 'SIN_DATO';
  const usd = currency === 'PEN' ? amount / penPerUsd : amount;
  if (usd < 100_000) return '<100k';
  if (usd < 150_000) return '100-150k';
  if (usd < 200_000) return '150-200k';
  if (usd < 300_000) return '200-300k';
  return '300k+';
}

export interface BuyerRow {
  leadId: string;
  dealType: 'SEPARACION' | 'VENTA';
  mediumCategory: string | null;
  district: string | null;
  birthDate: Date | null;
  contractMode: string | null;
  amount: number | null;
  currency: 'USD' | 'PEN';
  bedrooms: number | null;
  typology: string | null;
  purchasePurpose: string | null;
}

export interface BuyerBreakdown {
  key: string;
  count: number;
  pct: number;
}

export interface BuyerProfile {
  total: number;
  sales: number;
  separations: number;
  avgTicketUsd: number | null;
  byMedium: BuyerBreakdown[];
  byDistrict: BuyerBreakdown[];
  byAge: BuyerBreakdown[];
  byFinancing: BuyerBreakdown[];
  byTicket: BuyerBreakdown[];
  byBedrooms: BuyerBreakdown[];
  byTypology: BuyerBreakdown[];
  byPurpose: BuyerBreakdown[];
}

function breakdown(
  rows: readonly BuyerRow[],
  key: (r: BuyerRow) => string,
  order?: readonly string[],
) {
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(key(r), (counts.get(key(r)) ?? 0) + 1);
  const total = rows.length || 1;
  const list = [...counts.entries()].map(([k, count]) => ({
    key: k,
    count,
    pct: Math.round((count / total) * 1000) / 1000,
  }));
  if (order)
    return list.sort(
      (a, b) => order.indexOf(a.key as never) - order.indexOf(b.key as never) || b.count - a.count,
    );
  return list.sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}

/** Perfil del comprador de un proyecto / empresa: un comprador por lead (la venta manda sobre la separación). */
export function buyerProfile(
  input: readonly BuyerRow[],
  opts: { now?: Date; penPerUsd?: number } = {},
): BuyerProfile {
  const now = opts.now ?? new Date();
  const byLead = new Map<string, BuyerRow>();
  for (const r of input) {
    const prev = byLead.get(r.leadId);
    if (!prev || (prev.dealType === 'SEPARACION' && r.dealType === 'VENTA'))
      byLead.set(r.leadId, r);
  }
  const rows = [...byLead.values()];
  const tickets = rows
    .filter((r) => r.amount !== null && r.amount > 0)
    .map((r) => (r.currency === 'PEN' ? r.amount! / (opts.penPerUsd ?? 3.75) : r.amount!));
  return {
    total: rows.length,
    sales: rows.filter((r) => r.dealType === 'VENTA').length,
    separations: rows.filter((r) => r.dealType === 'SEPARACION').length,
    avgTicketUsd: tickets.length
      ? Math.round(tickets.reduce((s, v) => s + v, 0) / tickets.length)
      : null,
    byMedium: breakdown(rows, (r) => r.mediumCategory ?? 'SIN_MEDIO'),
    byDistrict: breakdown(rows, (r) => r.district ?? 'SIN_DATO'),
    byAge: breakdown(rows, (r) => ageBand(r.birthDate, now), AGE_BANDS),
    byFinancing: breakdown(rows, (r) => r.contractMode ?? 'SIN_DATO'),
    byTicket: breakdown(
      rows,
      (r) => ticketBand(r.amount, r.currency, opts.penPerUsd),
      TICKET_BANDS,
    ),
    byBedrooms: breakdown(rows, (r) => (r.bedrooms === null ? 'SIN_DATO' : `${r.bedrooms}D`)),
    byTypology: breakdown(rows, (r) => r.typology ?? 'SIN_DATO'),
    byPurpose: breakdown(rows, (r) => r.purchasePurpose ?? 'SIN_DEFINIR'),
  };
}

// ---------------------------------------------------------------- historial de separaciones y caídas

/**
 * Resultado de una separación: VENDIDA (llegó a venta activa), CAIDA (devolución), VIGENTE (sin resolver),
 * ANULADA (error de registro) o EXCLUIDA (motivo técnico / sin unidad contable). Solo las tres primeras
 * entran en los porcentajes, igual que en las métricas del embudo.
 */
export type SeparationOutcome = 'VIGENTE' | 'VENDIDA' | 'CAIDA' | 'ANULADA' | 'EXCLUIDA';

export const SEPARATION_OUTCOME_LABELS: Record<SeparationOutcome, string> = {
  VIGENTE: 'Vigente',
  VENDIDA: 'Vendida',
  CAIDA: 'Caída',
  ANULADA: 'Anulada',
  EXCLUIDA: 'Excluida',
};

export interface SeparationRow {
  id: string;
  leadId: string;
  projectId: string;
  projectName: string;
  contactName: string;
  advisorId: string | null;
  advisorName: string | null;
  /** ISO de `started_at`. */
  startedAt: string;
  units: string[];
  /** Moneda del precio (la de la unidad). */
  currency: string;
  /** Moneda de la reserva (`amountInitial`), propia desde el Lote 5 · H2 (P9). */
  initialCurrency: string;
  amountInitial: string | null;
  amountTotal: string | null;
  contractMode: string | null;
  bank: string | null;
  originalMedium: string | null;
  originalMediumCategory: string | null;
  outcome: SeparationOutcome;
  /** ISO de la venta activa que cerró esta separación. */
  soldAt: string | null;
  refundedAt: string | null;
  dropReason: string | null;
  dropReasonLabel: string | null;
  refundNotes: string | null;
  annulReason: string | null;
  /** Por qué no entra en la base (motivo de caída técnico o sin unidad contable); null si cuenta. */
  excludedReason: string | null;
  /** Días calendario desde la separación hasta la venta o la caída (null si sigue vigente). */
  daysToOutcome: number | null;
}

export interface SeparationGroupSummary {
  key: string;
  label: string;
  /** Separaciones que cuentan (vendidas + caídas + vigentes). */
  separations: number;
  sold: number;
  dropped: number;
  pending: number;
  annulled: number;
  excluded: number;
  /** caídas ÷ separaciones (null sin base). */
  dropRate: number | null;
  /** vendidas ÷ separaciones (null sin base). */
  closeRate: number | null;
  avgDaysToSale: number | null;
  avgDaysToDrop: number | null;
}

export interface SeparationsSummary {
  total: SeparationGroupSummary;
  byAdvisor: SeparationGroupSummary[];
  byProject: SeparationGroupSummary[];
  byMonth: SeparationGroupSummary[];
  byDropReason: Array<{ key: string; label: string; count: number; pct: number }>;
}

export interface SeparationOutcomeInput {
  deal: FunnelDeal;
  /** Venta no anulada del mismo lead que corresponde a esta separación (mismas unidades o misma fecha de inicio). */
  sale: Pick<FunnelDeal, 'finishedAt' | 'refundedAt' | 'annulledAt'> | null;
}

export function separationOutcome(
  { deal, sale }: SeparationOutcomeInput,
  config: FunnelConfig,
): SeparationOutcome {
  if (isAnnulledDeal(deal)) return 'ANULADA';
  if (!isSeparation(deal, config)) return 'EXCLUIDA';
  if (deal.refundedAt) return 'CAIDA';
  if (sale && sale.finishedAt && !sale.refundedAt && !isAnnulledDeal(sale)) return 'VENDIDA';
  return 'VIGENTE';
}

export function daysBetween(from: Date, to: Date): number {
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 86_400_000));
}

function emptyGroup(key: string, label: string): SeparationGroupSummary {
  return {
    key,
    label,
    separations: 0,
    sold: 0,
    dropped: 0,
    pending: 0,
    annulled: 0,
    excluded: 0,
    dropRate: null,
    closeRate: null,
    avgDaysToSale: null,
    avgDaysToDrop: null,
  };
}

function finishGroup(
  g: SeparationGroupSummary,
  days: { sale: number[]; drop: number[] },
): SeparationGroupSummary {
  const avg = (xs: number[]) =>
    xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : null;
  return {
    ...g,
    dropRate: g.separations ? g.dropped / g.separations : null,
    closeRate: g.separations ? g.sold / g.separations : null,
    avgDaysToSale: avg(days.sale),
    avgDaysToDrop: avg(days.drop),
  };
}

/** Agrega el historial de separaciones por asesor, proyecto, mes de separación y motivo de caída. */
export function summarizeSeparations(rows: SeparationRow[], tz?: string): SeparationsSummary {
  type Acc = { group: SeparationGroupSummary; days: { sale: number[]; drop: number[] } };
  const mk = (key: string, label: string): Acc => ({
    group: emptyGroup(key, label),
    days: { sale: [], drop: [] },
  });
  const total = mk('total', 'Total');
  const byAdvisor = new Map<string, Acc>();
  const byProject = new Map<string, Acc>();
  const byMonth = new Map<string, Acc>();
  const reasons = new Map<string, { label: string; count: number }>();
  const add = (acc: Acc, r: SeparationRow) => {
    const g = acc.group;
    if (r.outcome === 'ANULADA') g.annulled += 1;
    else if (r.outcome === 'EXCLUIDA') g.excluded += 1;
    else {
      g.separations += 1;
      if (r.outcome === 'VENDIDA') {
        g.sold += 1;
        if (r.daysToOutcome !== null) acc.days.sale.push(r.daysToOutcome);
      } else if (r.outcome === 'CAIDA') {
        g.dropped += 1;
        if (r.daysToOutcome !== null) acc.days.drop.push(r.daysToOutcome);
      } else g.pending += 1;
    }
  };
  for (const r of rows) {
    add(total, r);
    const adv = r.advisorId ?? 'sin-asesor';
    if (!byAdvisor.has(adv)) byAdvisor.set(adv, mk(adv, r.advisorName ?? 'Sin asesor'));
    add(byAdvisor.get(adv)!, r);
    if (!byProject.has(r.projectId)) byProject.set(r.projectId, mk(r.projectId, r.projectName));
    add(byProject.get(r.projectId)!, r);
    const m = monthKey(new Date(r.startedAt), tz);
    if (!byMonth.has(m)) byMonth.set(m, mk(m, m));
    add(byMonth.get(m)!, r);
    if (r.outcome === 'CAIDA') {
      const key = r.dropReason ?? 'SIN_MOTIVO';
      const cur = reasons.get(key) ?? { label: r.dropReasonLabel ?? key, count: 0 };
      cur.count += 1;
      reasons.set(key, cur);
    }
  }
  const finish = (m: Map<string, Acc>) => [...m.values()].map((a) => finishGroup(a.group, a.days));
  const dropped = total.group.dropped;
  return {
    total: finishGroup(total.group, total.days),
    byAdvisor: finish(byAdvisor).sort((a, b) => b.separations - a.separations),
    byProject: finish(byProject).sort((a, b) => b.separations - a.separations),
    byMonth: finish(byMonth).sort((a, b) => a.key.localeCompare(b.key)),
    byDropReason: [...reasons.entries()]
      .map(([key, v]) => ({
        key,
        label: v.label,
        count: v.count,
        pct: dropped ? v.count / dropped : 0,
      }))
      .sort((a, b) => b.count - a.count),
  };
}
